import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react'
import type { CandleData } from '../types/chartData'
import { botApi } from '../api'
import { getCandleCountForTimeframe } from '../../utils/chartUtils'
import { toApiTimestamp } from '../utils/timeParsing'

const SAVE_INTERVAL_MS = 5000

/**
 * Exported for useFormingBar, which subdivides `intervalMs` into animation steps.
 * Sharing it is what keeps the candle's build time equal to the bar's dwell time.
 */
export function getPlayTiming(speed: number): { intervalMs: number; batchSize: number } {
  if (speed >= 50) return { intervalMs: 100, batchSize: 10 }
  if (speed >= 25) return { intervalMs: 100, batchSize: 5 }
  if (speed >= 10) return { intervalMs: 100, batchSize: 2 }
  if (speed >= 5) return { intervalMs: 100, batchSize: 1 }
  if (speed >= 2) return { intervalMs: 250, batchSize: 1 }
  if (speed >= 1) return { intervalMs: 500, batchSize: 1 }
  return { intervalMs: 1000, batchSize: 1 }
}

/**
 * Index of the first candle strictly after `ts` — i.e. the next bar replay will
 * reveal. Exported so useFormingBar animates exactly that bar; two independent
 * copies of this search could disagree and form the wrong candle.
 */
export function binarySearchFirstAfter(candles: CandleData[], ts: number): number {
  let lo = 0
  let hi = candles.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (candles[mid].time <= ts) lo = mid + 1
    else hi = mid
  }
  return lo
}

export interface UsePlaybackOptions {
  btid: string
  rangeStartTs: number
  rangeEndTs: number
  progressViewTs: number
  onProgressViewChange: (ts: number) => void
  timeframe: string
  speed: number
  candlesRef: React.RefObject<CandleData[]>
  maxAvailableTsRef: React.RefObject<number | null>
  extendWindow: (direction: 'left' | 'right') => Promise<void>
  loadWindow: (centerDate: Date) => Promise<void>
  applyVisibleRange: (range: { from: number; to: number }) => void
  /**
   * Where reset() lands, and the floor jumpTo() clamps to. Defaults to rangeStartTs.
   *
   * The walkthrough overrides it: its range starts far before the drawings so indicators can
   * warm up, and restarting into that warm-up would replay hundreds of empty bars.
   */
  replayStartTs?: number
  /**
   * Wall-clock milliseconds per bar, overriding `getPlayTiming(speed).intervalMs`.
   *
   * Only the walkthrough's Tick detail sets it, where a bar's duration is a multiple of the
   * market clock rather than a speed bracket. It exists because the timing table tops out at
   * 1000ms per bar while tick's slowest pace needs one whole timeframe (60_000ms on M1,
   * 3_600_000 on H1). Also forces one bar per tick: batching several would be the opposite
   * of a live feel.
   */
  barIntervalMsOverride?: number | null
}

/** Options for a single jumpTo call. */
export interface JumpToOptions {
  /**
   * Skip the loadWindow when the target is already inside the loaded candles.
   *
   * jumpTo normally reloads and re-centres the window, which is right for a scrub across the
   * whole range but wrong for a one-bar step: each click would fire a network round-trip and
   * yank the viewport. Only honoured when the target really is in range.
   */
  keepWindow?: boolean
}

export interface UsePlaybackReturn {
  isPlaying: boolean
  play: () => void
  pause: () => void
  reset: () => void
  jumpTo: (ts: number, options?: JumpToOptions) => void
  saveNow: () => void
  playStartedAtMs: number | null
  playElapsedSec: number
}

export default function usePlayback({
  btid,
  rangeStartTs,
  rangeEndTs,
  progressViewTs,
  onProgressViewChange,
  timeframe,
  speed,
  candlesRef,
  maxAvailableTsRef,
  extendWindow,
  loadWindow,
  applyVisibleRange,
  replayStartTs,
  barIntervalMsOverride = null,
}: UsePlaybackOptions): UsePlaybackReturn {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playStartedAtMs, setPlayStartedAtMs] = useState<number | null>(null)
  const [playElapsedSec, setPlayElapsedSec] = useState(0)

  const isPlayingRef = useRef(false)
  const progressViewTsRef = useRef(progressViewTs)
  const lastSavedTsRef = useRef(progressViewTs)
  const speedRef = useRef(speed)
  const barIntervalMsRef = useRef(barIntervalMsOverride)
  const timeframeRef = useRef(timeframe)
  const playStartedAtMsRef = useRef<number | null>(null)
  /**
   * performance.now() when the pending bar's wait began, and what was left of it at the pause.
   *
   * A bar's reveal is a single armed timeout, so pausing and resuming would otherwise restart
   * that wait from zero — on tick detail, where one bar can be a whole timeframe, the user
   * watches the same candle build twice. Carrying the remainder makes pause a freeze rather
   * than a rewind, and it is what keeps the forming bar's own clock honest (useFormingBar
   * shifts its start by the paused duration to match).
   */
  const barStartedAtRef = useRef(0)
  const barRemainingMsRef = useRef<number | null>(null)
  // Pending debounced save (from rapid jumpTo calls). Cleared on commit/unmount.
  const pendingSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSaveTsRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    playStartedAtMsRef.current = playStartedAtMs
  }, [playStartedAtMs])

  // Accumulate the time spent in the current play session into playElapsedSec,
  // then clear the start marker. Safe to call when not currently playing (no-op).
  const accumulatePlayTime = useCallback(() => {
    const startedAt = playStartedAtMsRef.current
    if (startedAt == null) return
    const delta = (Date.now() - startedAt) / 1000
    playStartedAtMsRef.current = null
    setPlayStartedAtMs(null)
    if (delta > 0) {
      setPlayElapsedSec(prev => prev + delta)
    }
  }, [])

  useLayoutEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])
  useLayoutEffect(() => { progressViewTsRef.current = progressViewTs }, [progressViewTs])
  useLayoutEffect(() => { speedRef.current = speed }, [speed])
  useLayoutEffect(() => { barIntervalMsRef.current = barIntervalMsOverride }, [barIntervalMsOverride])
  useLayoutEffect(() => { timeframeRef.current = timeframe }, [timeframe])

  /**
   * The timing for this instant, honouring the wall-clock override.
   *
   * Read off the refs inside the loop rather than closed over, so a speed or detail change
   * takes hold on the next tick (the effect also restarts on both, which covers the gap
   * between now and that tick).
   */
  const timingNow = useCallback(() => {
    const override = barIntervalMsRef.current
    if (override != null && override > 0) return { intervalMs: override, batchSize: 1 }
    return getPlayTiming(speedRef.current)
  }, [])

  const saveToDB = useCallback((ts: number) => {
    if (ts === lastSavedTsRef.current) return
    // Cancel any pending debounced save — we're committing immediately.
    if (pendingSaveTimerRef.current) {
      clearTimeout(pendingSaveTimerRef.current)
      pendingSaveTimerRef.current = null
      pendingSaveTsRef.current = null
    }
    lastSavedTsRef.current = ts
    botApi.updateProgressView(btid, toApiTimestamp(ts)).catch(err => {
      if (import.meta.env.DEV) console.error('Failed to save progress_view:', err)
    })
  }, [btid])

  /**
   * Debounced save for rapid scrub interactions. Coalesces N quick clicks
   * into a single API call once the user pauses for SCRUB_SAVE_DEBOUNCE_MS.
   * The 5s periodic interval + unmount handler still flush any pending state
   * so we never lose progress.
   */
  const SCRUB_SAVE_DEBOUNCE_MS = 300
  const scheduleScrubSave = useCallback((ts: number) => {
    pendingSaveTsRef.current = ts
    if (pendingSaveTimerRef.current) clearTimeout(pendingSaveTimerRef.current)
    pendingSaveTimerRef.current = setTimeout(() => {
      pendingSaveTimerRef.current = null
      const pending = pendingSaveTsRef.current
      pendingSaveTsRef.current = null
      if (pending !== null) saveToDB(pending)
    }, SCRUB_SAVE_DEBOUNCE_MS)
  }, [saveToDB])

  // Periodic DB save every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const current = progressViewTsRef.current
      if (current !== lastSavedTsRef.current) {
        saveToDB(current)
      }
    }, SAVE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [saveToDB])

  // Save on unmount — also flush any pending debounced scrub save so the
  // last position the user dragged to is persisted before teardown.
  useEffect(() => {
    return () => {
      if (pendingSaveTimerRef.current) {
        clearTimeout(pendingSaveTimerRef.current)
        pendingSaveTimerRef.current = null
      }
      const current = progressViewTsRef.current
      if (current !== lastSavedTsRef.current) {
        saveToDB(current)
      }
      pendingSaveTsRef.current = null
    }
  }, [saveToDB])

  // ---- Play loop ----
  useEffect(() => {
    if (!isPlaying) return

    let cancelled = false
    let timerId: ReturnType<typeof setTimeout>
    let extensionInFlight = false

    /** Arm the next reveal and stamp the bar's wait, so a pause can measure what is left. */
    const arm = (delayMs: number) => {
      barStartedAtRef.current = performance.now()
      timerId = setTimeout(tick, delayMs)
    }

    const tick = () => {
      if (cancelled) return

      const candles = candlesRef.current ?? []
      const currentTs = progressViewTsRef.current
      const maxTs = maxAvailableTsRef.current
      const { batchSize } = timingNow()

      const nextIdx = binarySearchFirstAfter(candles, currentTs)

      if (nextIdx >= candles.length) {
        if (!extensionInFlight) {
          extensionInFlight = true
          extendWindow('right').finally(() => { extensionInFlight = false })
        }
        arm(200)
        return
      }

      // Cap the batch at the last buffered candle so we don't skip candles
      // that ARE available just because the batch endpoint runs past maxTs.
      let targetIdx = Math.min(nextIdx + batchSize - 1, candles.length - 1)
      if (maxTs !== null) {
        // Find the largest index whose candle.time <= maxTs.
        let lo = 0
        let hi = candles.length
        while (lo < hi) {
          const mid = (lo + hi) >> 1
          if (candles[mid].time <= maxTs) lo = mid + 1
          else hi = mid
        }
        const lastBufferedIdx = lo - 1
        if (lastBufferedIdx < nextIdx) {
          // Nothing buffered yet at/after currentTs; wait and retry.
          arm(500)
          return
        }
        targetIdx = Math.min(targetIdx, lastBufferedIdx)
      }

      const targetTs = candles[targetIdx].time

      if (targetTs > rangeEndTs) {
        accumulatePlayTime()
        setIsPlaying(false)
        return
      }

      onProgressViewChange(targetTs)

      const displayCount = targetIdx + 1
      const vc = getCandleCountForTimeframe(timeframeRef.current, displayCount)
      const lastVisibleIdx = displayCount - 1
      const from = Math.max(0, lastVisibleIdx - vc + 1)
      applyVisibleRange({ from, to: lastVisibleIdx })

      if (!extensionInFlight && candles.length > 0 && targetIdx > candles.length * 0.8) {
        extensionInFlight = true
        extendWindow('right').finally(() => { extensionInFlight = false })
      }

      const { intervalMs } = timingNow()
      arm(intervalMs)
    }

    // Resuming picks up what was left of the paused bar; anything else starts a fresh one.
    const { intervalMs } = timingNow()
    const remaining = barRemainingMsRef.current
    barRemainingMsRef.current = null
    arm(remaining != null ? Math.min(remaining, intervalMs) : intervalMs)

    return () => {
      cancelled = true
      clearTimeout(timerId)
    }
    // `speed` and `barIntervalMsOverride` are read through refs inside the loop, but are
    // listed so a change also restarts the pending timer — otherwise switching from
    // real-time to 5x would leave one last hour-long timeout armed.
  }, [isPlaying, speed, barIntervalMsOverride, timeframe, timingNow, candlesRef, maxAvailableTsRef, extendWindow, rangeEndTs, onProgressViewChange, applyVisibleRange, accumulatePlayTime])

  const play = useCallback(() => {
    // Idempotent: if we're already playing, don't reset playStartedAtMs (that
    // would silently zero out the elapsed counter in the status pill).
    if (isPlayingRef.current) return

    setIsPlaying(true)
    const now = Date.now()
    playStartedAtMsRef.current = now
    setPlayStartedAtMs(now)

    const currentTs = progressViewTsRef.current
    const candles = candlesRef.current ?? []
    const isCurrentWindowLoaded =
      candles.length > 0 &&
      currentTs >= candles[0].time &&
      currentTs <= candles[candles.length - 1].time

    if (!isCurrentWindowLoaded) {
      loadWindow(new Date(currentTs * 1000))
    }
  }, [candlesRef, loadWindow])

  const pause = useCallback(() => {
    // What is left of the bar being waited on, so resuming continues it rather than
    // restarting it. Clamped at both ends: a tab that slept through the whole wait resumes
    // immediately, and a pause with no armed bar behind it (barStartedAt still 0) must not
    // read as "the wait is already over" and reveal a bar the instant play is pressed.
    const { intervalMs } = timingNow()
    const spent = barStartedAtRef.current > 0 ? performance.now() - barStartedAtRef.current : 0
    barRemainingMsRef.current = Math.max(0, Math.min(intervalMs, intervalMs - spent))
    accumulatePlayTime()
    setIsPlaying(false)
    const current = progressViewTsRef.current
    if (current !== lastSavedTsRef.current) {
      saveToDB(current)
    }
  }, [saveToDB, accumulatePlayTime, timingNow])

  const reset = useCallback(() => {
    accumulatePlayTime()
    setPlayElapsedSec(0)
    setIsPlaying(false)
    // The cursor is about to move, so whatever was left of the paused bar is left of a bar
    // nobody is waiting on any more.
    barRemainingMsRef.current = null
    const target = replayStartTs ?? rangeStartTs
    onProgressViewChange(target)
    saveToDB(target)
    loadWindow(new Date(target * 1000))
  }, [rangeStartTs, replayStartTs, saveToDB, loadWindow, onProgressViewChange, accumulatePlayTime])

  // Any scrub (forward or backward) pauses the replay. This eliminates a race
  // where the play loop ticks against stale candles while loadWindow is in
  // flight for the new center. saveToDB is debounced so a rapid drag doesn't
  // hammer the API; the 5s periodic interval still backstops it.
  const jumpTo = useCallback((rawTs: number, options?: JumpToOptions) => {
    accumulatePlayTime()
    setIsPlaying(false)
    barRemainingMsRef.current = null
    // Floored at the replay start, not the range start: for a walkthrough those differ by
    // the server's ~200 bars of indicator warm-up, and a step-back or a scrub landing in
    // there would take the user somewhere the transport's own 0% cannot reach.
    const ts = Math.max(replayStartTs ?? rangeStartTs, rawTs)
    onProgressViewChange(ts)
    scheduleScrubSave(ts)

    // A step within the loaded window needs no refetch, and skipping it also avoids
    // loadWindow re-centring the viewport away from where the caller just scrolled to.
    if (options?.keepWindow) {
      const candles = candlesRef.current ?? []
      const inWindow = candles.length > 0
        && ts >= candles[0].time
        && ts <= candles[candles.length - 1].time
      if (inWindow) return
    }

    loadWindow(new Date(ts * 1000))
  }, [scheduleScrubSave, loadWindow, onProgressViewChange, accumulatePlayTime, candlesRef, replayStartTs, rangeStartTs])

  const saveNow = useCallback(() => {
    saveToDB(progressViewTsRef.current)
  }, [saveToDB])

  return {
    isPlaying,
    play,
    pause,
    reset,
    jumpTo,
    saveNow,
    playStartedAtMs,
    playElapsedSec,
  }
}
